import { useState } from 'react'
import { login, getMe } from '../../services/api'
import { User } from '../../types'
import { Eye, EyeOff } from 'lucide-react'

interface LoginProps {
  onLogin: (user: User) => void
}

export default function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await login(email, password)
      localStorage.setItem('dronemap_token', res.data.token)
      localStorage.setItem('dronemap_user', JSON.stringify(res.data.user))

      const meRes = await getMe()
      onLogin(meRes.data.user)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-900">
      <div className="w-full max-w-md">
        {/* Logo and Heading */}
        <div className="text-center mb-8">
          <div className="mb-6">
            {/* Place logo.png in web/public/ directory */}
            <img
              src="/logo.png"
              alt="DroneScape"
              className="h-20 mx-auto object-contain"
              onError={(e) => {
                // Fallback if logo.png is missing
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            DroneScape <span className="text-orange-500">–</span> Dronemaps
          </h1>
          <p className="text-navy-500 mt-2 text-sm">Aerial Intelligence Platform</p>
        </div>

        <div className="card bg-white/5 backdrop-blur-sm border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="label text-gray-300">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input bg-navy-800/50 border-navy-600 text-white placeholder-navy-500"
                placeholder="admin@dronemap.local"
                required
              />
            </div>

            <div>
              <label className="label text-gray-300">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input bg-navy-800/50 border-navy-600 text-white placeholder-navy-500 pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-navy-500 text-sm mt-6">
          © {new Date().getFullYear()} DRONESCAPE. All rights reserved.
        </p>
      </div>
    </div>
  )
}
